import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { CustomerAddressModelType } from '@teinor/erp/types/company/customer/customerAddress'
import ModalCECustomerAddress from './ModalCECustomerAddress'
import { ComponentWithPermissions } from '../../../../baseComponents/CreateRoutering/types'
import { permissionCheck } from '../../../../baseComponents/PermissionChecker/function'
import TwinTable from '../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderStateName, CustomRenderCountryName, CustomRenderBoolean, EditRow, DeleteRow } from '../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { getDictionaryCountries } from '../../../../utils/reducers/getters'
import ModalDeleteCustomerAddress from './ModalDeleteCustomerAddress'
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'

interface CustomerAddressesProps extends ComponentWithPermissions {
    basicData: CustomerModelType
}

const CustomerAddresses: React.FC<CustomerAddressesProps> = ({ basicData, userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('addresses', 'Direcciones'),
        newText: t('addAddress', 'Nueva dirección'),
    }

    return (
        <BaseScreenTableInner TableComponent={TableCustomerAddresses} CreateModalComponent={ModalCECustomerAddress} EditModalComponent={ModalCECustomerAddress} DeleteModalComponent={ModalDeleteCustomerAddress} pageData={pageData} userPermissions={userPermissions} extraWhereParams={{ CustomerId: basicData?.id }} basicData={basicData} />
    )
}
interface TableCustomerAddressesProps extends TableComponentBaseScreenInnerProps {
    className?: string
    name?: string
    hideLoadFiltersButton?: boolean
}

export const TableCustomerAddresses: React.FC<TableCustomerAddressesProps> = ({ setOpened, setTableInstance, userPermissions, extraWhereParams, ...rest}) => {
    const { t } = useTwinTranslation()
    const countries = getDictionaryCountries()


    const columns: ColumnTableSchema<CustomerAddressModelType> = [
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'address',
            dataKey: 'address',
            label: t('address', 'Dirección'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('address', 'Dirección'), }
            }
        },
        {
            id: 'additional_address',
            dataKey: 'additional_address',
            label: t('additional_address_short', 'Dirección ad.'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('additional_address', 'Dirección adicional'), }
            },
            minWidth: 160
        },
        {
            id: 'city',
            dataKey: 'city',
            label: t('city', 'Ciudad'),
            sortable: true,
            minWidth: 120,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('city', 'Ciudad'), }
            }
        },
        {
            id: 'state',
            dataKey: 'state',
            label: t('state', 'Provincia'),
            customRender: (parameterValue, allRowData) => <CustomRenderStateName stateId={parameterValue} allRowData={allRowData} />,
        },
        {
            id: 'country',
            dataKey: 'country',
            label: t('country', 'País'),
            customRender: (parameterValue) => <CustomRenderCountryName countryId={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: countries, label: t('country', 'País') }
            },
            generalSearchable: false
        },
        {
            id: 'fiscal_address',
            dataKey: 'fiscal_address',
            label: t('fiscalAddress', 'Dirección Fiscal'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={102} />,
            width: 120
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => {
                const icon = !allRowData?.active ? faRotateLeft : undefined
                return <DeleteRow key={'delete' + parameterValue} {...{ icon, parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />
            },
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    return (
        <TwinTable columns={columns} name='customerAddresses' getDataFrom='/api/app/customer/customerAddress/getAllCustomerAddresses'
             onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
             })} setTableInstance={setTableInstance} extraWhereParams={extraWhereParams} defaultOrder={{ defaultOrderField: 'fiscal_address', order: 'desc' }} {...rest} />
    )
}

export default CustomerAddresses